import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Send, Copy, Check, ChevronDown, Loader2, Mail, ShieldCheck,
  Trash2, Users, Clock, RefreshCw, RotateCcw, Eye, Lock, UserCog,
} from "lucide-react";
import { PremiumInput } from "../brand/PremiumInput";
import { api, apiError } from "../../lib/api";
import { useChainStore } from "../../data/chainStore";
import { ParticipantListSkeleton } from "../ui/operational-skeleton";

const PARTICIPANT_ROLES = [
  { id: "buyer",            label: "Buyer",              blurb: "Person purchasing the property." },
  { id: "seller",           label: "Seller",             blurb: "Vendor of the property." },
  { id: "buyer_solicitor",  label: "Buyer's Solicitor",  blurb: "Conveyancer acting for the buyer." },
  { id: "seller_solicitor", label: "Seller's Solicitor", blurb: "Conveyancer acting for the seller." },
  { id: "broker",           label: "Mortgage Broker",    blurb: "Adviser arranging buyer's finance." },
  { id: "solicitor",        label: "Solicitor (other)",  blurb: "Other legal counsel on the chain." },
];

const ROLE_LABEL = Object.fromEntries(PARTICIPANT_ROLES.map((r) => [r.id, r.label]));
const ROLE_BLURB = Object.fromEntries(PARTICIPANT_ROLES.map((r) => [r.id, r.blurb]));

/* iter-alignment · P3 · Deal Room · Participant Management (2026-02-28)
 *
 * Canonical v2 roles surfaced in the Right Rail and consumed by every
 * operational surface (Journey · Forecasting Centre · Deal Rooms
 * list · Contact Profile).  The Roles tab lets the operator assign,
 * reassign and remove these canonical rows without leaving the Deal
 * Room — every mutation flows through the shared
 * `/api/v2/deals/{id}/participants` endpoint set and emits a
 * dedicated `DealActivity` row.
 *
 * The vocabulary MIRRORS the backend enum verbatim
 * (`_PARTICIPANT_TYPE_ALLOWED` in `routes_persistence.py`).  Never
 * append here without a matching backend enum update. */
const CANONICAL_PARTICIPANT_ROLES = [
  { id: "buyer",            label: "Buyer" },
  { id: "seller",           label: "Seller" },
  { id: "buyer_solicitor",  label: "Buyer Solicitor" },
  { id: "seller_solicitor", label: "Seller Solicitor" },
  { id: "mortgage_broker",  label: "Mortgage Broker" },
  { id: "negotiator",       label: "Lead Agent" },
  { id: "progressor",       label: "Sales Progressor" },
  { id: "other",            label: "Other" },
];
const CANONICAL_ROLE_LABEL = Object.fromEntries(
  CANONICAL_PARTICIPANT_ROLES.map((r) => [r.id, r.label]),
);

// iter-184 — role → default visibility mapping mirrors the backend so the
// invite UI doesn't flicker waiting for the visibility-levels fetch.
const ROLE_DEFAULT_VISIBILITY = {
  buyer:            "buyer_access",
  seller:           "seller_access",
  buyer_solicitor:  "solicitor_access",
  seller_solicitor: "solicitor_access",
  solicitor:        "solicitor_access",
  broker:           "read_only",
};

// Calm tonal palette — soft tint backgrounds, ultra-light borders, premium
// operational typography. NEVER loud, NEVER enterprise admin styling.
const VISIBILITY_TONE = {
  internal:         { tint: "bg-indigo-50/70 text-indigo-700 ring-indigo-200/70" },
  full_access:      { tint: "bg-slate-50 text-slate-700 ring-slate-200/80" },
  solicitor_access: { tint: "bg-violet-50/70 text-violet-700 ring-violet-200/70" },
  buyer_access:     { tint: "bg-sky-50/70 text-sky-700 ring-sky-200/70" },
  seller_access:    { tint: "bg-emerald-50/70 text-emerald-700 ring-emerald-200/70" },
  read_only:        { tint: "bg-slate-50 text-slate-500 ring-slate-200/70" },
  restricted:       { tint: "bg-amber-50/70 text-amber-700 ring-amber-200/70" },
  observer:         { tint: "bg-slate-50 text-slate-500 ring-slate-200/70" },
};

const VISIBILITY_ICON = {
  internal:         ShieldCheck,
  read_only:        Eye,
  observer:         Eye,
  restricted:       Lock,
};

const VisibilityChip = ({ visibility, label, testid }) => {
  const tone = VISIBILITY_TONE[visibility] || VISIBILITY_TONE.full_access;
  const Icon = VISIBILITY_ICON[visibility] || ShieldCheck;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 h-[20px] rounded-full ring-1 text-[10.5px] font-medium tracking-tight ${tone.tint}`}
      data-testid={testid}
      data-visibility={visibility}
    >
      <Icon size={10.5} className="opacity-80" />
      {label || visibility}
    </span>
  );
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  const diff = Math.max(0, Date.now() - ms);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const initialsOf = (s) => {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const fullUrlFor = (invite_url) => {
  if (!invite_url) return "";
  return invite_url.startsWith("http") ? invite_url : `${window.location.origin}${invite_url}`;
};

/**
 * ParticipantsModal — opened from the Deal Room header.
 * - Compose: invite Buyer/Seller/Solicitors/Broker into THIS deal
 * - Pending: shows invite_url with copy + revoke
 * - Active: shows joined participants (avatars + roles)
 */
const AssignExistingContactPicker = ({ contacts, search, onSearch, selectedId, isOnDeal, onPick, onClear }) => {
  const q = String(search || "").trim().toLowerCase();
  const results = q.length < 2 ? [] : (Array.isArray(contacts) ? contacts : [])
    .filter((c) => `${c?.name || c?.fullName || ""} ${c?.email || ""}`.toLowerCase().includes(q))
    .slice(0, 6);
  if (selectedId) {
    return (
      <div className="mb-1 flex items-center justify-between rounded-lg bg-white border border-emerald-200 px-3 py-2" data-testid="participants-roles-assign-existing-selected">
        <span className="text-[12px] text-emerald-700 font-semibold">Linked to existing contact</span>
        <button type="button" onClick={onClear} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800" data-testid="participants-roles-assign-existing-clear">Change</button>
      </div>
    );
  }
  return (
    <div className="mb-1">
      <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3">Existing contact</label>
      <input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search contacts by name or email"
        className="w-full h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] text-slate-900"
        data-testid="participants-roles-assign-existing-search"
      />
      {results.length > 0 && (
        <ul className="mt-1 rounded-lg bg-white border border-slate-200 divide-y divide-slate-100" data-testid="participants-roles-assign-existing-results">
          {results.map((c) => {
            const onDeal = isOnDeal(c);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={onDeal}
                  onClick={() => onPick(c)}
                  className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-indigo-50/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`participants-roles-assign-existing-result-${c.id}`}
                >
                  <span className="font-semibold text-slate-800">{c.name || c.fullName}</span>
                  <span className="text-slate-500"> · {c.email || "no email"}{onDeal ? " · already on this deal" : ""}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-1.5 text-[11px] text-slate-500">Or fill in the details below to create a new contact.</p>
    </div>
  );
};

export const ParticipantsModal = ({ open, onClose, deal, property, initialRole }) => {
  const dealId = deal?.id;
  const propertyAddress = property?.address || deal?.name || "this deal";

  const [tab, setTab] = useState("invite"); // invite | pending | active | roles
  const [invites, setInvites] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);

  /* iter-alignment · P3 · Deal Room · Participant Management
   * (2026-02-28) — canonical v2 participants (the operational rows
   * the Right Rail + Journey read from).  Kept as a distinct state
   * slice from the Mongo-backed `participants` list because they
   * live in different tables and satisfy different UX needs. */
  const [canonicalParticipants, setCanonicalParticipants] = useState([]);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [roleMenuFor, setRoleMenuFor] = useState(null);
  const [rowSubmittingFor, setRowSubmittingFor] = useState(null);
  const [confirmRemoveFor, setConfirmRemoveFor] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    participant_type: "buyer",
    full_name: "",
    email: "",
    phone: "",
    organisation_name: "",
  });
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignContactId, setAssignContactId] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignExistingMatch, setAssignExistingMatch] = useState(null);

  // iter-184 — visibility levels fetched once from the backend so the
  // picker labels stay in sync with the canonical vocabulary.
  const [visibilityLevels, setVisibilityLevels] = useState([]);

  // Compose state
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("buyer");
  const [fullName, setFullName] = useState("");
  const [roleOpen, setRoleOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // last successful invite (for inline copy state)
  const [copiedFor, setCopiedFor] = useState(null);
  const [resendingFor, setResendingFor] = useState(null);
  const inputRef = useRef(null);

  /* iter-alignment · P0 · Add Existing Contact (2026-02-19)
   * -------------------------------------------------------
   * Compact typeahead over the CANONICAL Contacts SSOT
   * (`useChainStore.contacts`).  Selecting a contact pre-fills
   * the email + full-name inputs, then hands off to the SAME
   * invite submit path — the backend already dedups Contact
   * identity by email, so no duplicate Contact record is ever
   * created and no parallel API is added.  Duplicate check
   * against the canonical participants list blocks a second
   * add on the same deal.  Existing "Invite new contact" form
   * remains untouched below this section. */
  const contactsFromStore = useChainStore().contacts;
  const [contactSearch, setContactSearch] = useState("");
  const [linkedContactId, setLinkedContactId] = useState(null);
  const _norm = (s) => String(s || "").trim().toLowerCase();
  const contactMatches = useMemo(() => {
    const q = _norm(contactSearch);
    if (!q || q.length < 2) return [];
    const arr = Array.isArray(contactsFromStore) ? contactsFromStore : [];
    return arr
      .filter((c) => {
        const name = _norm(c?.name || c?.fullName);
        const em   = _norm(c?.email);
        return name.includes(q) || em.includes(q);
      })
      .slice(0, 6);
  }, [contactSearch, contactsFromStore]);
  const _isAlreadyOnDeal = useCallback((contact) => {
    if (!contact) return false;
    const targetEmail = _norm(contact.email);
    const targetId    = String(contact.id || "");
    const rows = Array.isArray(canonicalParticipants) ? canonicalParticipants : [];
    return rows.some((p) => {
      if (p?.contact_id && targetId && String(p.contact_id) === targetId) return true;
      if (p?.email && _norm(p.email) === targetEmail) return true;
      return false;
    });
  }, [canonicalParticipants]);
  /* iter-alignment · P2 · Contact Role Prefill (2026-02-28)
   * -----------------------------------------------------------
   * Soft-prefill the deal-scoped `role` state when the operator
   * picks an existing Contact whose canonical Contacts SSOT role
   * is unambiguous.  Reuses the existing role vocab already
   * defined in this file (`PARTICIPANT_ROLES` ids) — no new
   * state, no new endpoint, no duplicate role logic.
   *
   * Mapping (canonical Contacts role → modal `role` id):
   *   • "Solicitor"              → "solicitor"
   *   • "Mortgage Broker" / "Broker" → "broker"
   *   • "Buyer"                  → "buyer"
   *   • "Seller"                 → "seller"
   *
   * The prefill NEVER overwrites a role the operator has already
   * manually selected (guarded by `roleManuallySetRef`).  When
   * the canonical role is missing, ambiguous or unmapped, the
   * current role selection is left unchanged. */
  const _prefillRoleFromContact = useCallback((contact) => {
    const raw = String(contact?.role || contact?.type || "").trim().toLowerCase();
    if (!raw) return null;
    if (raw === "solicitor" || raw.endsWith(" solicitor")) return "solicitor";
    if (raw === "mortgage broker" || raw === "broker" || raw.endsWith(" broker")) return "broker";
    if (raw === "buyer")  return "buyer";
    if (raw === "seller") return "seller";
    return null;
  }, []);
  const roleManuallySetRef = useRef(false);
  const _setRoleManually = useCallback((next) => {
    roleManuallySetRef.current = true;
    setRole(next);
  }, []);

  const handleContactPick = useCallback((contact) => {
    if (!contact) return;
    if (_isAlreadyOnDeal(contact)) {
      // iter-alignment · P2 · Add Existing Contact wording aligned
      // to the pilot spec ("this transaction" — the operator's
      // mental model — not "this Deal Room").
      setError("Already added to this transaction.");
      toast.error("Already added to this transaction.");
      return;
    }
    setError("");
    setEmail(contact.email || "");
    setFullName(contact.name || contact.fullName || "");
    setLinkedContactId(contact.id || null);
    setContactSearch("");
    // iter-alignment · P2 · Contact Role Prefill (2026-02-28) —
    // Soft-prefill the deal-scoped role from the canonical
    // Contacts SSOT.  Guarded by `roleManuallySetRef` so a role
    // the operator has already chosen stays put; ambiguous /
    // unmapped roles leave the current selection unchanged.
    if (!roleManuallySetRef.current) {
      const prefill = _prefillRoleFromContact(contact);
      if (prefill) setRole(prefill);
    }
  }, [_isAlreadyOnDeal, _prefillRoleFromContact]);

  /* P3 · Broker Auto-Prompt (2026-02-28) — pre-select the role
   * when the modal is opened from the Mortgage Broker Required
   * banner.  `initialRole` is optional; when supplied the
   * canonical Roles tab opens seeded with the target role so the
   * operator lands directly on the assignment surface.  Purely
   * additive — every other opener still lands on the default tab. */
  useEffect(() => {
    if (!open || !initialRole) return;
    setTab("roles");
    setAssignOpen(true);
    setAssignForm((f) => ({ ...f, participant_type: initialRole }));
  }, [open, initialRole]);

  /* iter-alignment · P2 · Contact Role Prefill (2026-02-28) —
   * Reset the manual-role tracking flag every time the modal is
   * (re)opened so a fresh pick can soft-prefill again.  If the
   * caller passes `initialRole` (broker auto-prompt path), we
   * count that as an explicit manual pre-selection so the
   * subsequent contact pick does NOT overwrite it. */
  useEffect(() => {
    if (!open) return;
    roleManuallySetRef.current = !!initialRole;
  }, [open, initialRole]);

  // iter-184 — visibility picker state. `visibilityTouched` tracks whether
  // the operator has explicitly changed the visibility (so we keep
  // following the role's default until they do).
  const [visibility, setVisibility] = useState("full_access");
  const [visibilityTouched, setVisibilityTouched] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [editingVisibilityFor, setEditingVisibilityFor] = useState(null);

  const visibilityLabel = useMemo(() => {
    const found = visibilityLevels.find((v) => v.id === visibility);
    return found?.label || visibility;
  }, [visibility, visibilityLevels]);

  const reset = () => {
    setTab("invite");
    setEmail("");
    setRole("buyer");
    setFullName("");
    setRoleOpen(false);
    setSubmitting(false);
    setError("");
    setCreated(null);
    setCopiedFor(null);
    setResendingFor(null);
    setVisibility(ROLE_DEFAULT_VISIBILITY.buyer || "full_access");
    setVisibilityTouched(false);
    setVisibilityOpen(false);
    setEditingVisibilityFor(null);
    /* iter-alignment · P3 — reset Roles-tab UI state. */
    setRoleMenuFor(null);
    setRowSubmittingFor(null);
    setConfirmRemoveFor(null);
    setAssignOpen(false);
    setAssignForm({
      participant_type: "buyer",
      full_name: "",
      email: "",
      phone: "",
      organisation_name: "",
    });
    setAssignSubmitting(false);
    setAssignError("");
    setAssignContactId(null);
    setAssignSearch("");
    setAssignExistingMatch(null);
  };

  // iter-184 — when the operator changes role and hasn't manually picked a
  // visibility yet, follow the role-default so the picker reads sensibly.
  useEffect(() => {
    if (visibilityTouched) return;
    setVisibility(ROLE_DEFAULT_VISIBILITY[role] || "full_access");
  }, [role, visibilityTouched]);

  // Fetch the canonical visibility vocabulary once when the modal opens.
  useEffect(() => {
    if (!open || visibilityLevels.length > 0) return;
    let alive = true;
    api.get("/api/deal-invites/visibility-levels")
      .then(({ data }) => { if (alive && Array.isArray(data)) setVisibilityLevels(data); })
      .catch(() => { /* calm degradation — defaults already wired */ });
    return () => { alive = false; };
  }, [open, visibilityLevels.length]);

  const fetchAll = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const [{ data: invs }, { data: parts }] = await Promise.all([
        api.get(`/api/deals/${dealId}/participant-invites`),
        api.get(`/api/deals/${dealId}/participants`),
      ]);
      setInvites(Array.isArray(invs) ? invs : []);
      setParticipants(Array.isArray(parts) ? parts : []);
    } catch (err) {
      toast.error("Couldn't load participants", { description: apiError(err) });
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  /* iter-alignment · P3 — canonical v2 participant fetch.  Kept as a
   * separate effect so a slow Postgres query never blocks the invite
   * / active tabs; and so refreshing the Roles tab alone doesn't
   * re-hit Mongo. */
  const fetchCanonicalParticipants = useCallback(async () => {
    if (!dealId) return;
    setCanonicalLoading(true);
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/participants`);
      setCanonicalParticipants(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Couldn't load canonical participants", { description: apiError(err) });
    } finally {
      setCanonicalLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    if (!open) return;
    reset();
    if (initialRole) {
      setTab("roles");
      setAssignOpen(true);
      setAssignForm((f) => ({ ...f, participant_type: initialRole }));
    }
    fetchAll();
    fetchCanonicalParticipants();
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open, initialRole, fetchAll, fetchCanonicalParticipants]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !deal) return null;

  /* iter-alignment · P0 · Add Existing Contact as Primary Path
   * (2026-02-28)
   * -----------------------------------------------------------------
   * `PARTICIPANT_ROLES` uses the legacy invite-form vocabulary
   * (`broker` / `solicitor`), the canonical v2 participants
   * endpoint uses the enum (`mortgage_broker` / `other`).  Map
   * once here so an existing-contact attach lands in the correct
   * bucket without the operator ever seeing a 400. */
  const _ROLE_TO_PARTICIPANT_TYPE = {
    broker: "mortgage_broker",
    solicitor: "other",
  };

  const submit = async (e) => {
    e?.preventDefault();
    /* iter-alignment · P0 · Add existing contact is the primary
     * path (2026-02-28).  When the operator picked a Contact from
     * the typeahead we attach them directly via the canonical
     * `/api/v2/deals/{id}/participants` endpoint with `contact_id`
     * — the backend's `link_participant_async` idempotently binds
     * the row to the existing Contact so we NEVER create a
     * duplicate.  Access/invite settings (visibility, invite URL)
     * only apply on the fresh-invite branch below. */
    if (linkedContactId) {
      const cleaned = email.trim().toLowerCase();
      const name = fullName.trim();
      if (!name) {
        setError("Full name is required");
        return;
      }
      // Belt-and-braces duplicate guard against the canonical roster.
      const alreadyOnDeal = (Array.isArray(canonicalParticipants) ? canonicalParticipants : [])
        .some((p) => String(p?.contact_id || "") === String(linkedContactId));
      if (alreadyOnDeal) {
        setError("Already added to this transaction.");
        toast.error("Already added to this transaction.");
        return;
      }
      setSubmitting(true);
      setError("");
      try {
        const body = {
          participant_type: _ROLE_TO_PARTICIPANT_TYPE[role] || role,
          full_name: name,
          contact_id: linkedContactId,
        };
        if (cleaned) body.email = cleaned;
        const { data } = await api.post(`/api/v2/deals/${dealId}/participants`, body);
        setCanonicalParticipants((prev) => [...prev, data]);
        toast.success("Added to Deal Room", {
          description: `${name} joined as ${CANONICAL_ROLE_LABEL[body.participant_type] || body.participant_type}.`,
        });
        // Reset compose fields for the next add.
        setEmail("");
        setRole("buyer");
        setFullName("");
        setVisibility(ROLE_DEFAULT_VISIBILITY.buyer || "full_access");
        setVisibilityTouched(false);
        setContactSearch("");
        setLinkedContactId(null);
        // iter-alignment · P2 · Contact Role Prefill (2026-02-28) —
        // Reset the manual-role flag after a successful add so the
        // next contact pick can prefill again from the canonical
        // SSOT role.
        roleManuallySetRef.current = false;
        emitCanonicalChanged();
      } catch (err) {
        const msg = apiError(err);
        setError(msg);
        toast.error("Couldn't add participant", { description: msg });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) {
      setError("Enter a valid email address");
      return;
    }
    /* iter-alignment · P0 · Add Existing Contact — belt-and-braces
     * duplicate block.  The typeahead already blocks a re-add of a
     * canonical Contact, but the operator may have typed the same
     * email into the invite form manually.  Compare against the
     * canonical participants list so we never fire a duplicate
     * invite. */
    const alreadyAttached = (Array.isArray(canonicalParticipants) ? canonicalParticipants : [])
      .some((p) => String(p?.email || "").trim().toLowerCase() === cleaned);
    if (alreadyAttached) {
      setError("Already added to this transaction.");
      toast.error("Already added to this transaction.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = { email: cleaned, role, visibility };
      if (fullName.trim()) body.full_name = fullName.trim();
      const { data } = await api.post(`/api/deals/${dealId}/participant-invites`, body);
      setCreated(data);
      // Optimistic prepend
      setInvites((prev) => [data, ...prev.filter((p) => p.id !== data.id)]);
      setTab("pending");
      toast.success("Invite ready", {
        description: `${cleaned} can now join as ${data.role_label} · ${data.visibility_label || data.visibility}.`,
      });
      // Reset compose fields for "invite another"
      setEmail("");
      setRole("buyer");
      setFullName("");
      setVisibility(ROLE_DEFAULT_VISIBILITY.buyer || "full_access");
      setVisibilityTouched(false);
      // Clear the existing-contact typeahead state after a
      // successful invite so the operator can add another cleanly.
      setContactSearch("");
      setLinkedContactId(null);
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error("Couldn't send invite", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async (invite) => {
    const url = fullUrlFor(invite.invite_url);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFor(invite.id);
      toast.success("Invite link copied");
      setTimeout(() => setCopiedFor((c) => (c === invite.id ? null : c)), 1800);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const revoke = async (invite) => {
    try {
      await api.delete(`/api/deals/${dealId}/participant-invites/${invite.id}`);
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      toast.success("Invite revoked");
    } catch (err) {
      toast.error("Couldn't revoke invite", { description: apiError(err) });
    }
  };

  // iter-184 — adjust a live participant's visibility level. Only the
  // owning org will succeed (backend enforces). Optimistic UI; reverts
  // on failure.
  const changeVisibility = async (participant, nextVisibility) => {
    if (!participant || !nextVisibility || participant.visibility === nextVisibility) {
      setEditingVisibilityFor(null);
      return;
    }
    const prior = participant.visibility;
    setParticipants((prev) => prev.map((p) =>
      p.id === participant.id ? { ...p, visibility: nextVisibility } : p,
    ));
    setEditingVisibilityFor(null);
    try {
      const { data } = await api.patch(
        `/api/deals/${dealId}/participants/${participant.id}/visibility`,
        { visibility: nextVisibility },
      );
      setParticipants((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      toast.success("Access updated", {
        description: `${participant.full_name || participant.email} now has ${data.visibility_label || data.visibility} access.`,
      });
    } catch (err) {
      setParticipants((prev) => prev.map((p) =>
        p.id === participant.id ? { ...p, visibility: prior } : p,
      ));
      toast.error("Couldn't update access", { description: apiError(err) });
    }
  };

  const resend = async (invite) => {
    setResendingFor(invite.id);
    try {
      const { data } = await api.post(`/api/deals/${dealId}/participant-invites`, {
        email: invite.email,
        role: invite.role,
        // iter-184 — preserve the original visibility on resend so the
        // operator doesn't have to re-select it.
        ...(invite.visibility ? { visibility: invite.visibility } : {}),
        ...(invite.full_name ? { full_name: invite.full_name } : {}),
      });
      // Backend refreshes the same row (rotates token + extends expiry); merge by id.
      setInvites((prev) => {
        const without = prev.filter((i) => i.id !== data.id && i.id !== invite.id);
        return [data, ...without];
      });
      toast.success("Invite resent", {
        description: `Fresh link emailed to ${invite.email}.`,
      });
    } catch (err) {
      toast.error("Couldn't resend invite", { description: apiError(err) });
    } finally {
      setResendingFor(null);
    }
  };

  /* iter-alignment · P3 · Deal Room · Participant Management
   * (2026-02-28) — canonical v2 mutations.  Each op PATCHes /
   * DELETEs against `/api/v2/deals/{id}/participants` so the Right
   * Rail + Journey + Activity Feed re-derive in the next render.
   * Every op refreshes the canonical list AND fires a soft
   * `cf:participants-changed` custom event so the parent Deal
   * Room page can refetch its own `participants` prop without
   * needing to prop-drill a callback in. */
  const emitCanonicalChanged = () => {
    if (typeof window === "undefined") return;
    try {
      window.dispatchEvent(new CustomEvent("cf:participants-changed", { detail: { dealId } }));
    } catch { /* browsers without CustomEvent constructor · no-op */ }
  };

  const reassignRole = async (participant, nextType) => {
    if (!participant || !nextType || participant.participant_type === nextType) {
      setRoleMenuFor(null);
      return;
    }
    setRoleMenuFor(null);
    setRowSubmittingFor(participant.id);
    try {
      const { data } = await api.patch(
        `/api/v2/deals/${dealId}/participants/${participant.id}`,
        { participant_type: nextType },
      );
      setCanonicalParticipants((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      toast.success("Role reassigned", {
        description: `${participant.full_name} is now the ${CANONICAL_ROLE_LABEL[nextType] || nextType}.`,
      });
      emitCanonicalChanged();
    } catch (err) {
      toast.error("Couldn't reassign role", { description: apiError(err) });
    } finally {
      setRowSubmittingFor(null);
    }
  };

  const removeCanonical = async (participant) => {
    if (!participant) return;
    setConfirmRemoveFor(null);
    setRowSubmittingFor(participant.id);
    try {
      await api.delete(`/api/v2/deals/${dealId}/participants/${participant.id}`);
      setCanonicalParticipants((prev) => prev.filter((p) => p.id !== participant.id));
      toast.success("Participant removed", {
        description: `${participant.full_name} is no longer on this deal.`,
      });
      emitCanonicalChanged();
    } catch (err) {
      toast.error("Couldn't remove participant", { description: apiError(err) });
    } finally {
      setRowSubmittingFor(null);
    }
  };

  const submitAssign = async (e) => {
    e?.preventDefault();
    const name = String(assignForm.full_name || "").trim();
    if (!name) {
      setAssignError("Full name is required");
      return;
    }
    const emailRaw = String(assignForm.email || "").trim().toLowerCase();
    if (emailRaw && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRaw)) {
      setAssignError("Enter a valid email address or leave it blank");
      return;
    }
    setAssignError("");
    setAssignSubmitting(true);
    try {
      const body = {
        participant_type: assignForm.participant_type,
        full_name: name,
      };
      if (assignContactId) {
        body.contact_id = assignContactId;
      } else {
        if (emailRaw) body.email = emailRaw;
        if (assignForm.phone.trim()) body.phone = assignForm.phone.trim();
        if (assignForm.organisation_name.trim()) body.organisation_name = assignForm.organisation_name.trim();
        body.strict_new_contact = true;
      }
      const { data } = await api.post(`/api/v2/deals/${dealId}/participants`, body);
      setCanonicalParticipants((prev) => [...prev, data]);
      toast.success("Participant assigned", {
        description: `${name} joined as ${CANONICAL_ROLE_LABEL[assignForm.participant_type] || assignForm.participant_type}.`,
      });
      setAssignOpen(false);
      setAssignForm({
        participant_type: "buyer",
        full_name: "",
        email: "",
        phone: "",
        organisation_name: "",
      });
      setAssignContactId(null);
      setAssignExistingMatch(null);
      emitCanonicalChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "contact_details_match_existing") {
        setAssignExistingMatch(detail.contact_id ? { id: detail.contact_id, name: detail.contact_name, email: detail.contact_email } : null);
        setAssignError(detail.message);
        return;
      }
      const msg = apiError(err);
      setAssignError(msg);
      toast.error("Couldn't assign participant", { description: msg });
    } finally {
      setAssignSubmitting(false);
    }
  };

  const pendingInvites = invites.filter((i) => i.status === "pending");
  const acceptedInvites = invites.filter((i) => i.status === "accepted");

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[6vh] bg-slate-950/55 backdrop-blur-sm"
      data-testid="participants-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-[640px] rounded-2xl bg-white border border-slate-200/80 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.32)] overflow-hidden"
        data-testid="participants-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Aurora */}
        <div className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-300/35 to-blue-300/0 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 w-48 h-48 rounded-full bg-gradient-to-br from-fuchsia-300/30 to-indigo-200/0 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-[0_8px_20px_-8px_rgba(99, 102, 241,0.6)]">
            <Users size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-[19px] font-bold tracking-tight text-slate-900">
              Deal Room participants
            </h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug truncate">
              Invite buyers, sellers, solicitors and brokers into <span className="font-semibold text-slate-700">{propertyAddress}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="participants-modal-close"
            className="-mt-1 -mr-1 w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="relative px-6 -mt-1 mb-4">
          <div className="inline-flex rounded-xl bg-slate-100 p-1 text-[12px] font-semibold">
            {[
              { id: "invite",  label: "Invite",  icon: Mail,  count: null },
              { id: "pending", label: "Pending", icon: Clock, count: pendingInvites.length },
              { id: "active",  label: "Active",  icon: ShieldCheck, count: participants.length },
              { id: "roles",   label: "Roles",   icon: UserCog, count: canonicalParticipants.length },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3.5 h-8 rounded-lg inline-flex items-center gap-1.5 transition-all ${
                  tab === t.id
                    ? "bg-white text-slate-900 shadow-[0_2px_6px_-1px_rgba(15,23,42,0.10)]"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`participants-tab-${t.id}`}
              >
                <t.icon size={12} /> {t.label}
                {typeof t.count === "number" && t.count > 0 && (
                  <span className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] ${
                    tab === t.id ? "bg-indigo-100 text-indigo-700" : "bg-slate-200 text-slate-600"
                  }`}>{t.count}</span>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { fetchAll(); fetchCanonicalParticipants(); }}
              className="ml-1 px-2 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white inline-flex items-center"
              data-testid="participants-refresh"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw size={12} className={loading || canonicalLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="relative px-6 pb-6 max-h-[62vh] overflow-y-auto">
          {tab === "invite" && (
            <>
              {/* iter-alignment · P0 · Add Existing Contact —
                  compact typeahead over the canonical Contacts
                  SSOT.  Selecting pre-fills the invite form below
                  and blocks duplicate participant links. */}
              <div className="mb-4" data-testid="participants-add-existing">
                <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                  Add existing contact
                </label>
                <PremiumInput
                  type="text"
                  placeholder="Search Contacts by name or email"
                  value={contactSearch}
                  onChange={(v) => { setContactSearch(v); setLinkedContactId(null); }}
                  autoComplete="off"
                  size="md"
                  testid="participants-add-existing-input"
                />
                {contactMatches.length > 0 && (
                  <div
                    className="mt-1.5 rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up max-h-[220px] overflow-y-auto"
                    data-testid="participants-add-existing-results"
                  >
                    {contactMatches.map((c) => {
                      const already = _isAlreadyOnDeal(c);
                      return (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => handleContactPick(c)}
                          disabled={already}
                          className={`w-full text-left px-3.5 py-2.5 flex items-center justify-between gap-3 transition-colors ${already ? "opacity-60 cursor-not-allowed" : "hover:bg-indigo-50/60"}`}
                          data-testid={`participants-add-existing-result-${c.id}`}
                        >
                          <span className="flex flex-col min-w-0">
                            <span className="text-[13px] font-semibold text-slate-900 truncate">{c.name || c.fullName || "—"}</span>
                            <span className="text-[11.5px] text-slate-500 truncate">{c.email || "—"}</span>
                          </span>
                          {already ? (
                            <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">Already added to this transaction</span>
                          ) : (
                            <span className="text-[11px] font-semibold text-indigo-600 whitespace-nowrap">Add →</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* iter-alignment · P2 · Add Existing Contact (spec)
                    (2026-02-28) — when the operator's search returns
                    no canonical Contact match, surface an explicit
                    "+ Create new contact" affordance that scrolls
                    focus into the invite form below.  The invite
                    form itself is the existing SSOT-safe write path
                    (`POST /api/deals/{id}/participant-invites`); this
                    is just a signposted entry point so operators
                    never have to guess. */}
                {contactSearch.trim().length >= 2 && contactMatches.length === 0 && !linkedContactId && (
                  <div
                    className="mt-1.5 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 flex items-center justify-between gap-3"
                    data-testid="participants-add-existing-empty"
                  >
                    <span className="text-[12px] text-slate-500 leading-snug">
                      No matching contact. You can create a new one below.
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Seed the invite form with whatever the
                        // operator typed so they don't have to
                        // retype an email / name.  We do not clear
                        // `contactSearch` — leaving the query
                        // visible confirms which term is being used
                        // to seed the new contact record.
                        const q = contactSearch.trim();
                        const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(q);
                        if (looksLikeEmail) setEmail(q);
                        else if (!fullName) setFullName(q);
                        setLinkedContactId(null);
                        setTimeout(() => inputRef.current?.focus(), 40);
                      }}
                      className="shrink-0 text-[11.5px] font-semibold text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
                      data-testid="participants-add-existing-create-new"
                    >
                      + Create new contact
                    </button>
                  </div>
                )}
                {linkedContactId && (
                  <p className="mt-1.5 text-[11.5px] text-emerald-600 font-medium" data-testid="participants-add-existing-linked">
                    Linked to existing Contact — set role and access below.
                  </p>
                )}
              </div>

            <form onSubmit={submit} data-testid="participants-invite-form" noValidate>
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Email
              </label>
              <PremiumInput
                ref={inputRef}
                type="email"
                placeholder="participant@example.co.uk"
                value={email}
                onChange={setEmail}
                autoComplete="off"
                size="lg"
                testid="participants-email-input"
              />

              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-4">
                Role on this deal
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setRoleOpen((o) => !o)}
                  className="w-full h-11 pl-3.5 pr-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-left flex items-center justify-between transition-all"
                  data-testid="participants-role-trigger"
                >
                  <span className="flex flex-col">
                    <span className="text-[13.5px] font-semibold text-slate-900">{ROLE_LABEL[role]}</span>
                    <span className="text-[11px] text-slate-500 truncate">{ROLE_BLURB[role]}</span>
                  </span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${roleOpen ? "rotate-180" : ""}`} />
                </button>
                {roleOpen && (
                  <div className="absolute left-0 right-0 mt-2 z-10 rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up max-h-[260px] overflow-y-auto" data-testid="participants-role-menu">
                    {PARTICIPANT_ROLES.map((r) => (
                      <button
                        type="button"
                        key={r.id}
                        onClick={() => { _setRoleManually(r.id); setRoleOpen(false); }}
                        className={`w-full text-left px-3.5 py-2.5 hover:bg-indigo-50/60 flex flex-col gap-0.5 transition-colors ${role === r.id ? "bg-indigo-50/80" : ""}`}
                        data-testid={`participants-role-${r.id}`}
                      >
                        <span className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
                          {r.label}
                          {role === r.id && <Check size={13} className="text-indigo-600" />}
                        </span>
                        <span className="text-[11px] text-slate-500">{r.blurb}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-4">
                Full name <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
              </label>
              <PremiumInput
                type="text"
                placeholder="Olivia Marsh"
                value={fullName}
                onChange={setFullName}
                autoComplete="off"
                size="lg"
                testid="participants-name-input"
              />

              {/* iter-184 — visibility picker. Calm, operationally obvious,
                  never enterprise admin styling. */}
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-4">
                Access level
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setVisibilityOpen((o) => !o)}
                  className="w-full h-11 pl-3.5 pr-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-left flex items-center justify-between transition-all"
                  data-testid="participants-visibility-trigger"
                  data-visibility={visibility}
                >
                  <span className="flex flex-col min-w-0">
                    <span className="text-[13.5px] font-semibold text-slate-900 truncate">
                      {visibilityLabel}
                    </span>
                    <span className="text-[11px] text-slate-500 truncate">
                      {(visibilityLevels.find((v) => v.id === visibility)?.blurb) ||
                        "Defaults to the recommended access for this role."}
                    </span>
                  </span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${visibilityOpen ? "rotate-180" : ""}`} />
                </button>
                {visibilityOpen && (
                  <div
                    className="absolute left-0 right-0 mt-2 z-10 rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up max-h-[260px] overflow-y-auto"
                    data-testid="participants-visibility-menu"
                  >
                    {(visibilityLevels.length > 0 ? visibilityLevels : Object.keys(VISIBILITY_TONE).map((id) => ({ id, label: id, blurb: "" })))
                      .map((v) => (
                      <button
                        type="button"
                        key={v.id}
                        onClick={() => {
                          setVisibility(v.id);
                          setVisibilityTouched(true);
                          setVisibilityOpen(false);
                        }}
                        className={`w-full text-left px-3.5 py-2.5 hover:bg-indigo-50/60 flex flex-col gap-0.5 transition-colors ${visibility === v.id ? "bg-indigo-50/80" : ""}`}
                        data-testid={`participants-visibility-${v.id}`}
                      >
                        <span className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
                          {v.label}
                          {visibility === v.id && <Check size={13} className="text-indigo-600" />}
                        </span>
                        {v.blurb && (
                          <span className="text-[11px] text-slate-500 leading-snug">{v.blurb}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-3 text-[12.5px] text-rose-600" data-testid="participants-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full h-11 rounded-xl text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(135deg, #6366F1 0%, #6366F1 50%, #3B82F6 100%)",
                  boxShadow: "0 10px 28px -10px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
                }}
                data-testid="participants-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {linkedContactId ? "Adding…" : "Sending invite…"}
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    {linkedContactId ? "Add to Deal Room" : "Send invite"}
                  </>
                )}
              </button>
              <p className="mt-3 text-[11px] text-slate-400 leading-snug">
                {linkedContactId
                  ? "Adds the existing contact to this Deal Room. Access invite is separate — send one from the Access tab if this participant needs their own login."
                  : "Invitees create their own ChainFlow account and get scoped access to this Deal Room only. Invites expire after 14 days."}
              </p>

              {created && (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-3 flex items-start gap-3" data-testid="participants-last-created">
                  <ShieldCheck size={16} className="text-emerald-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] text-emerald-800 leading-snug">
                      Invite for <span className="font-semibold">{created.email}</span> ({created.role_label}) is ready.
                    </p>
                    <button
                      type="button"
                      onClick={() => setTab("pending")}
                      className="text-[12px] font-semibold text-emerald-700 hover:underline mt-1"
                      data-testid="participants-view-pending"
                    >
                      View pending invite →
                    </button>
                  </div>
                </div>
              )}
            </form>
            </>
          )}

          {tab === "pending" && (
            <div className="space-y-2.5" data-testid="participants-pending-list">
              {loading && pendingInvites.length === 0 && (
                <ParticipantListSkeleton rows={3} avatar={36} testid="participants-pending-loading" />
              )}
              {!loading && pendingInvites.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center">
                  <Clock size={22} className="text-slate-400 mx-auto mb-2" />
                  <p className="text-[13px] font-semibold text-slate-700">No pending invites</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">Send one from the Invite tab to get started.</p>
                </div>
              )}
              {pendingInvites.map((inv) => {
                const url = fullUrlFor(inv.invite_url);
                return (
                  <div
                    key={inv.id}
                    className="rounded-xl border border-slate-200 bg-white p-3.5"
                    data-testid={`participants-pending-${inv.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                        {initialsOf(inv.full_name || inv.email)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-slate-900 truncate">{inv.full_name || inv.email}</p>
                        <p className="text-[11.5px] text-slate-500 truncate">
                          {inv.email} · <span className="font-semibold text-indigo-700">{inv.role_label}</span> · sent {fmtRel(inv.created_at)}
                        </p>
                        {inv.visibility && (
                          <div className="mt-1.5">
                            <VisibilityChip
                              visibility={inv.visibility}
                              label={inv.visibility_label}
                              testid={`participants-pending-visibility-${inv.id}`}
                            />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => resend(inv)}
                        disabled={resendingFor === inv.id}
                        className="text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 w-8 h-8 rounded-lg inline-flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`participants-resend-${inv.id}`}
                        aria-label="Resend invite"
                        title="Resend invite (fresh link · extends expiry)"
                      >
                        {resendingFor === inv.id
                          ? <Loader2 size={13} className="animate-spin" />
                          : <RotateCcw size={13} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => revoke(inv)}
                        className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 w-8 h-8 rounded-lg inline-flex items-center justify-center transition-colors"
                        data-testid={`participants-revoke-${inv.id}`}
                        aria-label="Revoke invite"
                        title="Revoke invite"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {url && (
                      <div className="mt-3 flex items-stretch gap-2">
                        <input
                          readOnly
                          value={url}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 text-[11.5px] text-slate-700 font-mono outline-none"
                          data-testid={`participants-link-${inv.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => copyLink(inv)}
                          className={`h-9 px-3 rounded-lg border text-[11.5px] font-semibold inline-flex items-center gap-1.5 transition-all ${
                            copiedFor === inv.id
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-slate-900 hover:bg-slate-800 border-slate-900 text-white"
                          }`}
                          data-testid={`participants-copy-${inv.id}`}
                        >
                          {copiedFor === inv.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {acceptedInvites.length > 0 && (
                <p className="text-[11px] text-slate-400 text-center pt-2">
                  {acceptedInvites.length} earlier {acceptedInvites.length === 1 ? "invite" : "invites"} already accepted — see the Active tab.
                </p>
              )}
            </div>
          )}

          {tab === "active" && (
            <div className="space-y-2.5" data-testid="participants-active-list">
              {loading && participants.length === 0 && (
                <ParticipantListSkeleton rows={3} avatar={40} testid="participants-active-loading" />
              )}
              {!loading && participants.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center">
                  <Users size={22} className="text-slate-400 mx-auto mb-2" />
                  <p className="text-[13px] font-semibold text-slate-700">No participants yet</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">Once someone accepts an invite, they&apos;ll show up here.</p>
                </div>
              )}
              {participants.map((p) => (
                <div
                  key={p.id}
                  className="rounded-xl border border-slate-200 bg-white p-3.5"
                  data-testid={`participants-active-${p.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
                      {initialsOf(p.full_name || p.email)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-slate-900 truncate">{p.full_name || p.email}</p>
                      <p className="text-[11.5px] text-slate-500 truncate">
                        {p.email} · <span className="font-semibold text-indigo-700">{p.role_label}</span> · joined {fmtRel(p.joined_at)}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10.5px] font-semibold">
                      <ShieldCheck size={11} /> Active
                    </span>
                  </div>
                  {/* iter-184 — visibility chip with calm inline edit. */}
                  <div className="mt-2.5 flex items-center justify-between gap-2 relative">
                    <VisibilityChip
                      visibility={p.visibility}
                      label={p.visibility_label}
                      testid={`participants-active-visibility-${p.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setEditingVisibilityFor(
                        (curr) => (curr === p.id ? null : p.id),
                      )}
                      className="text-[11px] font-semibold text-slate-500 hover:text-indigo-700 transition-colors px-2 h-6 rounded-md hover:bg-indigo-50/60"
                      data-testid={`participants-active-edit-visibility-${p.id}`}
                    >
                      Change access
                    </button>
                    {editingVisibilityFor === p.id && (
                      <div
                        className="absolute right-0 top-8 z-10 w-[240px] rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up max-h-[260px] overflow-y-auto"
                        data-testid={`participants-active-visibility-menu-${p.id}`}
                      >
                        {(visibilityLevels.length > 0 ? visibilityLevels : Object.keys(VISIBILITY_TONE).map((id) => ({ id, label: id, blurb: "" })))
                          .map((v) => (
                          <button
                            type="button"
                            key={v.id}
                            onClick={() => changeVisibility(p, v.id)}
                            className={`w-full text-left px-3 py-2 hover:bg-indigo-50/60 flex flex-col gap-0.5 transition-colors ${p.visibility === v.id ? "bg-indigo-50/80" : ""}`}
                            data-testid={`participants-active-visibility-option-${p.id}-${v.id}`}
                          >
                            <span className="text-[12.5px] font-semibold text-slate-900 flex items-center gap-2">
                              {v.label}
                              {p.visibility === v.id && <Check size={12} className="text-indigo-600" />}
                            </span>
                            {v.blurb && (
                              <span className="text-[10.5px] text-slate-500 leading-snug">{v.blurb}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "roles" && (
            <div className="space-y-3" data-testid="participants-roles-list">
              <div
                className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5"
                data-testid="participants-roles-doctrine"
              >
                <p className="text-[12px] text-slate-600 leading-snug">
                  <span className="font-semibold text-slate-800">Canonical participants.</span>{" "}
                  These rows drive the Right Rail, Journey and Activity Feed. Assign, reassign or
                  remove people directly here — every change is captured on the deal timeline.
                </p>
              </div>

              {canonicalLoading && canonicalParticipants.length === 0 && (
                <ParticipantListSkeleton rows={3} avatar={40} testid="participants-roles-loading" />
              )}

              {!canonicalLoading && canonicalParticipants.length === 0 && !assignOpen && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center">
                  <UserCog size={22} className="text-slate-400 mx-auto mb-2" />
                  <p className="text-[13px] font-semibold text-slate-700">No participants yet</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    Assign the buyer, seller, solicitors and broker to unlock the Journey rail.
                  </p>
                </div>
              )}

              {canonicalParticipants.map((p) => {
                const isSubmitting = rowSubmittingFor === p.id;
                const confirming = confirmRemoveFor === p.id;
                const roleMenuOpen = roleMenuFor === p.id;
                return (
                  <div
                    key={p.id}
                    className="rounded-xl border border-slate-200 bg-white p-3.5"
                    data-testid={`participants-role-row-${p.id}`}
                    data-participant-type={p.participant_type}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
                        {initialsOf(p.full_name || p.email || "?")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-slate-900 truncate">
                          {p.full_name || p.email || "Unnamed"}
                        </p>
                        <p className="text-[11.5px] text-slate-500 truncate">
                          {p.email || "no email"} ·{" "}
                          <span className="font-semibold text-indigo-700">
                            {CANONICAL_ROLE_LABEL[p.participant_type] || p.participant_type}
                          </span>
                          {p.organisation_name && <> · {p.organisation_name}</>}
                        </p>
                      </div>
                      {isSubmitting && (
                        <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />
                      )}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2 relative">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setRoleMenuFor((curr) => (curr === p.id ? null : p.id))}
                          disabled={isSubmitting}
                          className="text-[11px] font-semibold text-slate-500 hover:text-indigo-700 transition-colors px-2 h-6 rounded-md hover:bg-indigo-50/60 inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid={`participants-role-change-${p.id}`}
                        >
                          <UserCog size={11} /> Change role
                          <ChevronDown size={11} className={`transition-transform ${roleMenuOpen ? "rotate-180" : ""}`} />
                        </button>
                        {roleMenuOpen && (
                          <div
                            className="absolute left-0 top-8 z-10 w-[220px] rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up max-h-[260px] overflow-y-auto"
                            data-testid={`participants-role-menu-${p.id}`}
                          >
                            {CANONICAL_PARTICIPANT_ROLES.map((r) => (
                              <button
                                type="button"
                                key={r.id}
                                onClick={() => reassignRole(p, r.id)}
                                className={`w-full text-left px-3 py-2 hover:bg-indigo-50/60 flex items-center justify-between transition-colors ${p.participant_type === r.id ? "bg-indigo-50/80" : ""}`}
                                data-testid={`participants-role-option-${p.id}-${r.id}`}
                              >
                                <span className="text-[12.5px] font-semibold text-slate-900">{r.label}</span>
                                {p.participant_type === r.id && <Check size={12} className="text-indigo-600" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {confirming ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-[11px] text-rose-600 font-semibold">Remove?</span>
                          <button
                            type="button"
                            onClick={() => removeCanonical(p)}
                            disabled={isSubmitting}
                            className="h-6 px-2 rounded-md text-[11px] font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60"
                            data-testid={`participants-role-remove-confirm-${p.id}`}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveFor(null)}
                            className="h-6 px-2 rounded-md text-[11px] font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            data-testid={`participants-role-remove-cancel-${p.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveFor(p.id)}
                          disabled={isSubmitting}
                          className="text-[11px] font-semibold text-slate-500 hover:text-rose-600 transition-colors px-2 h-6 rounded-md hover:bg-rose-50 inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid={`participants-role-remove-${p.id}`}
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {!assignOpen && (
                <button
                  type="button"
                  onClick={() => setAssignOpen(true)}
                  className="w-full h-11 rounded-xl border border-dashed border-slate-300 bg-white hover:bg-indigo-50/40 hover:border-indigo-300 text-[12.5px] font-semibold text-slate-600 hover:text-indigo-700 inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="participants-roles-assign-open"
                >
                  <UserCog size={14} /> Assign participant
                </button>
              )}

              {assignOpen && (
                <form
                  onSubmit={submitAssign}
                  className="rounded-xl border border-indigo-200/70 bg-indigo-50/40 p-4"
                  data-testid="participants-roles-assign-form"
                  noValidate
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[12.5px] font-bold text-slate-800">Assign a participant</p>
                    <button
                      type="button"
                      onClick={() => { setAssignOpen(false); setAssignError(""); }}
                      className="text-slate-400 hover:text-slate-700 w-6 h-6 rounded inline-flex items-center justify-center"
                      data-testid="participants-roles-assign-cancel"
                      aria-label="Cancel"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                    Role
                  </label>
                  <select
                    value={assignForm.participant_type}
                    onChange={(e) => setAssignForm((f) => ({ ...f, participant_type: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] text-slate-900"
                    data-testid="participants-roles-assign-role"
                  >
                    {CANONICAL_PARTICIPANT_ROLES.map((r) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>

                  <AssignExistingContactPicker
                    contacts={contactsFromStore}
                    search={assignSearch}
                    onSearch={setAssignSearch}
                    selectedId={assignContactId}
                    isOnDeal={_isAlreadyOnDeal}
                    onPick={(c) => {
                      setAssignContactId(c.id);
                      setAssignExistingMatch(null);
                      setAssignError("");
                      setAssignSearch("");
                      setAssignForm((f) => ({ ...f, full_name: c.name || c.fullName || "", email: c.email || "", phone: c.phone || "", organisation_name: c.company || "" }));
                    }}
                    onClear={() => {
                      setAssignContactId(null);
                      setAssignForm((f) => ({ ...f, full_name: "", email: "", phone: "", organisation_name: "" }));
                    }}
                  />
                  <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3">
                    {assignContactId ? "Full name (existing contact)" : "Full name (new contact)"}
                  </label>
                  <PremiumInput
                    type="text"
                    placeholder="Olivia Marsh"
                    value={assignForm.full_name}
                    onChange={(v) => setAssignForm((f) => ({ ...f, full_name: v }))}
                    autoComplete="off"
                    testid="participants-roles-assign-name"
                  />

                  <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3">
                    Email <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
                  </label>
                  <PremiumInput
                    type="email"
                    placeholder="participant@example.co.uk"
                    value={assignForm.email}
                    onChange={(v) => setAssignForm((f) => ({ ...f, email: v }))}
                    autoComplete="off"
                    testid="participants-roles-assign-email"
                  />

                  <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3">
                    Phone <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
                  </label>
                  <PremiumInput
                    type="text"
                    placeholder="+44 7700 900123"
                    value={assignForm.phone}
                    onChange={(v) => setAssignForm((f) => ({ ...f, phone: v }))}
                    autoComplete="off"
                    testid="participants-roles-assign-phone"
                  />

                  <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3">
                    Firm / organisation <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
                  </label>
                  <PremiumInput
                    type="text"
                    placeholder="Marsh & Co Solicitors"
                    value={assignForm.organisation_name}
                    onChange={(v) => setAssignForm((f) => ({ ...f, organisation_name: v }))}
                    autoComplete="off"
                    testid="participants-roles-assign-organisation"
                  />

                  {assignError && (
                    <p
                      className="mt-3 text-[12.5px] text-rose-600"
                      data-testid="participants-roles-assign-error"
                    >
                      {assignError}
                    </p>
                  )}
                  {assignExistingMatch && (
                    <button
                      type="button"
                      onClick={() => {
                        setAssignContactId(assignExistingMatch.id);
                        setAssignForm((f) => ({ ...f, full_name: assignExistingMatch.name || f.full_name, email: assignExistingMatch.email || f.email }));
                        setAssignExistingMatch(null);
                        setAssignError("");
                      }}
                      className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white border border-indigo-200 text-[12px] font-semibold text-indigo-700 hover:bg-indigo-50"
                      data-testid="participants-roles-assign-use-existing"
                    >
                      Use existing contact · {assignExistingMatch.name}
                    </button>
                  )}

                  <button
                    type="submit"
                    disabled={assignSubmitting}
                    className="mt-4 w-full h-10 rounded-xl text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: "linear-gradient(135deg, #6366F1 0%, #6366F1 50%, #3B82F6 100%)",
                      boxShadow: "0 10px 28px -10px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
                    }}
                    data-testid="participants-roles-assign-submit"
                  >
                    {assignSubmitting
                      ? (<><Loader2 size={13} className="animate-spin" /> Assigning…</>)
                      : (<><UserCog size={13} /> Assign to deal</>)}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantsModal;
